import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, AlertCircle, Target, CheckCircle2 } from "lucide-react";

/**
 * LearnerGraph — redesigned as a clean "Today's memory" dashboard card.
 * - A single sparkline showing CEFR drift over weeks
 * - 6 compact signal rows with values and deltas
 * - One headline recommendation card on the right
 */

const SIGNALS = [
  { key: "vocab", label: "Vocabulary", value: 78, delta: "+4", tone: "up" },
  { key: "gram", label: "Grammar", value: 62, delta: "+2", tone: "up" },
  { key: "pron", label: "Pronunciation", value: 71, delta: "—", tone: "flat" },
  { key: "flue", label: "Fluency", value: 55, delta: "+6", tone: "up" },
  { key: "conf", label: "Confidence", value: 48, delta: "-3", tone: "down" },
  { key: "list", label: "Listening", value: 83, delta: "+1", tone: "up" },
];

const SPARK_POINTS = [34, 38, 42, 47, 49, 52, 54, 55, 58, 61, 63, 66];

const LearnerGraph = () => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        let start = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - start) / 1400);
          setProgress(p);
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.25 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  const max = Math.max(...SPARK_POINTS);
  const min = Math.min(...SPARK_POINTS);
  const w = 640;
  const h = 140;
  const pad = 12;
  const pts = SPARK_POINTS.map((v, i) => {
    const x = pad + (i / (SPARK_POINTS.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / (max - min)) * (h - pad * 2);
    return [x, y];
  });

  const pathLen = Math.round(w * 1.4);
  const path = pts.map((p, i) => (i === 0 ? `M ${p[0]},${p[1]}` : `L ${p[0]},${p[1]}`)).join(" ");
  const area = `${path} L ${pts[pts.length - 1][0]},${h - pad} L ${pts[0][0]},${h - pad} Z`;

  return (
    <section id="graph" className="relative py-28 px-6" data-testid="section-learner-graph">
      <div className="max-w-7xl mx-auto">
        <div className="section-label">Living learner graph</div>
        <div className="mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <h2 className="headline text-4xl sm:text-5xl lg:text-[72px] max-w-3xl">
            Your mind, <em>at a glance</em>.
          </h2>
          <p className="text-whisper text-[16px] leading-[1.55] max-w-md">
            A dashboard of how you sound today — not a test score.
          </p>
        </div>

        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mt-12 glass rounded-[28px] edge-glow p-6 sm:p-10 relative overflow-hidden"
          data-testid="learner-graph-card"
        >
          <div aria-hidden className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full bg-aura-cyan/15 blur-[140px]" />

          <div className="relative grid lg:grid-cols-12 gap-8">
            {/* LEFT — CEFR drift + signals */}
            <div className="lg:col-span-8">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">CEFR trajectory · 12 weeks</div>
                  <div className="mt-1 flex items-end gap-3">
                    <div className="font-display text-[44px] font-light tracking-tight text-ink leading-none">B1.4</div>
                    <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-aura-cyan flex items-center gap-1 pb-1.5">
                      <TrendingUp size={12} /> +0.6 this month
                    </div>
                  </div>
                </div>
                <div className="pill">Memory loaded {Math.round(progress * 100)}%</div>
              </div>

              {/* Sparkline */}
              <div className="mt-6 rounded-2xl border border-panel/8 bg-elev/60 p-4">
                <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="sparkFill" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="rgb(var(--accent-cyan-rgb) / 0.35)" />
                      <stop offset="100%" stopColor="rgb(var(--accent-cyan-rgb) / 0)" />
                    </linearGradient>
                  </defs>
                  {/* gridlines */}
                  {[0.25, 0.5, 0.75].map((f) => (
                    <line key={f} x1={pad} x2={w - pad} y1={h * f} y2={h * f} stroke="rgb(var(--panel-rgb) / 0.08)" strokeDasharray="2 4" />
                  ))}
                  <path d={area} fill="url(#sparkFill)" opacity={progress} />
                  <path
                    d={path}
                    fill="none"
                    stroke="rgb(var(--accent-cyan-rgb))"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      strokeDasharray: pathLen,
                      strokeDashoffset: pathLen * (1 - progress),
                      filter: "drop-shadow(0 0 8px rgb(var(--accent-cyan-rgb) / 0.5))",
                    }}
                  />
                  {pts.map((p, i) => (
                    <circle key={i} cx={p[0]} cy={p[1]} r={i === pts.length - 1 ? 4 : 2} fill="rgb(var(--accent-cyan-rgb))" opacity={progress} />
                  ))}
                </svg>
                <div className="mt-2 flex justify-between font-mono text-[10px] tracking-[0.2em] uppercase text-whisper-muted">
                  {["W1", "W3", "W6", "W9", "W12"].map((l) => <span key={l}>{l}</span>)}
                </div>
              </div>

              {/* Signals */}
              <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="learner-signals">
                {SIGNALS.map((s) => (
                  <div key={s.key} className="hairline rounded-xl p-3.5 bg-elev/50">
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">{s.label}</div>
                      <div
                        className={`font-mono text-[10.5px] tracking-[0.1em] uppercase ${
                          s.tone === "up" ? "text-aura-cyan" : s.tone === "down" ? "text-red-300" : "text-whisper-muted"
                        }`}
                      >
                        {s.delta}
                      </div>
                    </div>
                    <div className="mt-2 flex items-end gap-2">
                      <div className="font-display text-[22px] font-light text-ink leading-none tabular-nums">{s.value}</div>
                      <div className="flex-1 h-1.5 rounded-full bg-panel/5 overflow-hidden mb-1">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${s.value * progress}%`,
                            background:
                              "linear-gradient(90deg, rgb(var(--accent-cyan-rgb)), rgb(var(--accent-blue-rgb)))",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — next move */}
            <div className="lg:col-span-4 flex flex-col gap-3">
              <div className="rounded-2xl node-card p-5">
                <div className="relative z-10 flex items-center gap-2 font-mono text-[10.5px] tracking-[0.22em] uppercase text-aura-cyan">
                  <AlertCircle size={12} /> weak spot
                </div>
                <div className="relative z-10 mt-2 font-display text-[22px] font-light text-ink leading-tight">Past subjunctive</div>
                <div className="relative z-10 mt-1 text-whisper text-[13.5px]">Missed 3 times this week. Scheduled for Thursday.</div>
              </div>
              <div className="rounded-2xl hairline p-5 bg-elev/50">
                <div className="flex items-center gap-2 font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">
                  <Target size={12} /> goal
                </div>
                <div className="mt-2 font-display text-[18px] font-light text-ink leading-tight">B2 by July · Berlin relocation</div>
                <div className="mt-3 h-1.5 rounded-full bg-panel/5 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-aura-cyan to-aura-violet" style={{ width: `${64 * progress}%` }} />
                </div>
                <div className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">64% of the way there</div>
              </div>
              <div className="rounded-2xl hairline p-5 bg-elev/50">
                <div className="flex items-center gap-2 font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">
                  <CheckCircle2 size={12} /> next session
                </div>
                <div className="mt-2 font-display text-[18px] font-light text-ink leading-tight">Mock negotiation · 10 min</div>
                <div className="mt-1 text-whisper text-[13.5px]">Reuses: market scene · past simple.</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LearnerGraph;
